/**
 * The LiveKit room's data-topic names - the hub's shared-conventions table
 * (Documentation/remote-assistance/README.md), mirrored verbatim by the assist
 * console (`assist-console/src/protocol/messages.ts`), the vision server's room
 * client, and the Unity client (`AssistProtocol.cs`). All payloads are UTF-8
 * JSON; reliable/ordered publishes unless a topic says otherwise.
 */
export const RoomTopics = {
  /** vision -> wearer/assistants: the existing detections payload, plus ready/server-health/training events the DC carries today. */
  detections: 'detections',
  /** vision (Server authority) or wearer (Local authority) -> all: the training wire messages; also carries the wearer's upstream training messages. */
  training: 'training',
  /** wearer, vision, each assistant -> all: existing client/server health payloads verbatim (+ assistantHealth, Plan-03). */
  health: 'health',
  /** assistants -> wearer (+ vision for logging): stroke messages (Plan-04). */
  strokes: 'strokes',
  /** wearer + assistants -> all: join intent, AV state, observer flag, wearer hide/mute hints. */
  presence: 'presence',
} as const;

export type RoomTopic = (typeof RoomTopics)[keyof typeof RoomTopics];

/** Room participant identities fixed by the hub conventions. */
export const RoomIdentities = {
  /** The single headset. */
  wearer: 'wearer',
  /** The detection server (participant metadata `{"role":"vision"}`) - filtered from rosters. */
  vision: 'vision',
  /** Prefix of assistant identities (`assistant:<slug>#<rand4>`). */
  assistantPrefix: 'assistant:',
} as const;

/** Is this identity an assistant (by the hub's identity convention)? */
export function isAssistantIdentity(identity: string): boolean {
  return identity.startsWith(RoomIdentities.assistantPrefix);
}
